
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import clsxm from '@/lib/clsxm';
import { getPublicCompletions } from '@/data/completions';
import { getCompletionsFromPublicGenerations } from '@/data/public_completions';
import { SectionHeading, tone } from '@/components/agents/primitives';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import Seo from '@/components/Seo';
import { type RelatedTemplate, type TemplateExample, type TemplateRecommendation, useTemplateTranslation, } from '@/components/templates/landing/kit';
import { rankTemplateExamples } from '@/components/templates/landing/rankExamples';
import { getTemplateSampleSet, type TemplateSampleSet, } from '@/components/templates/landing/sampleSets';
import TemplateExamples from '@/components/templates/landing/TemplateExamples';
import TemplateFaq, { type FaqItem, } from '@/components/templates/landing/TemplateFaq';
import TemplateFinalCta from '@/components/templates/landing/TemplateFinalCta';
import TemplateHero from '@/components/templates/landing/TemplateHero';
import TemplateHowItWorks from '@/components/templates/landing/TemplateHowItWorks';
import TemplateNextSteps from '@/components/templates/landing/TemplateNextSteps';
import TemplateRelated from '@/components/templates/landing/TemplateRelated';
import TemplateUseCases, { type UseCaseItem, } from '@/components/templates/landing/TemplateUseCases';
import TemplateWorkspace from '@/components/templates/landing/TemplateWorkspace';
import { getRecommendationsForPreset } from '@/constants/presetRecommendations';
import { CLINICAL_PRESET_SLUGS, Preset, PresetCategory, presets, } from '@/constants/presets';
// These two corpora total ~1MB of string literals. They are referenced ONLY
// inside getStaticProps (Next.js strips them from the client bundle) — never
// pass them to the client or import them from client components.
import { templateFaqs } from '@/constants/templateFaqs';
import { templateUseCases } from '@/constants/templateUseCases';
import { useUser } from '@/utils/useUser';
const Footer = dynamic(() => import('@/components/layout/Footer'));
// Client only: the wall measures itself and plays videos.
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const SocialStoriesBotPromo = dynamic(() => import('@/components/SocialStoriesBotPromo'));
const SocialStoryExample = dynamic(() => import('@/components/SocialStoryExample'));
interface TemplateProps {
    completions: TemplateExample[];
    preset: Preset;
    locale?: string;
    otherPresets: RelatedTemplate[];
    recommendations?: TemplateRecommendation[];
    samples?: TemplateSampleSet | null;
    useCases?: UseCaseItem[];
    faqs?: FaqItem[];
    isClinical?: boolean;
}
const Template = ({ completions = [], preset, otherPresets = [], recommendations = [], samples = null, useCases = [], faqs = [], isClinical = false, }: TemplateProps) => {
    const router = useRouter();
    const { t, lang } = useTemplateTranslation();
    const { user } = useUser();
    useEffect(() => {
        if (!preset)
            return;
        try {
            const stored = JSON.parse(localStorage.getItem('favourites') || '[]');
            if (Array.isArray(stored) && !stored.includes(preset.slug)) {
                localStorage.setItem('favourites', JSON.stringify([...stored, preset.slug]));
            }
        }
        catch {
            // storage unavailable or malformed: nothing to remember
        }
        if (preset.isAbsoluteURL) {
            // These presets live on their own landing page.
            router.push(preset.landingPageSlug as string);
        }
    }, [preset, router]);
    // Signed-in visitors get the full tool in the dashboard instead.
    useEffect(() => {
        if (!user || !preset)
            return;
        router.push(`/presets/${preset.slug}`, `/presets/${preset.slug}`, {
            locale: lang,
        });
    }, [user, preset, router, lang]);
    if (!preset)
        return null;
    const title = t(preset.title) || preset.title;
    const isSocialStories = lang === 'en' && preset.slug === 'social-stories-generator';
    const examples = completions.filter((completion) => typeof completion.completion === 'string' &&
        completion.completion.trim().length > 0);
    // The workspace shows the first example (visual templates show their
    // samples there instead); the examples section shows the rest.
    const [featured, ...rest] = examples;
    const sectionExamples = samples ? examples : rest;
    const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Easy-Peasy.AI',
                item: 'https://easy-peasy.ai/',
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: t('Templates'),
                item: `https://easy-peasy.ai${lang === 'en' ? '' : `/${lang}`}/templates`,
            },
            { '@type': 'ListItem', position: 3, name: title },
        ],
    };
    return (<main dir={lang === 'ar' ? 'rtl' : undefined} className={clsxm('relative overflow-x-clip', tone.page)}>
      <Seo templateTitle={lang === 'en'
            ? `${preset.isFree ? 'Free ' : ''}${preset.title.includes('AI') ? '' : 'AI '}${title}`
            : title} description={lang === 'en'
            ? `${t(preset.description)}${preset.isFree ? ' Use it for Free.' : ''}`
            : t(preset.description)} image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(title)}&description=${encodeURIComponent(t(preset.description))}`}/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumb).replace(/</g, '\\u003c'),
        }}/>
      </Head>

      <TemplateHero preset={preset}>
        <div id='tool' className='scroll-mt-6'>
          <TemplateWorkspace preset={preset} example={featured} samples={samples}/>
        </div>
      </TemplateHero>

      <TeamsLogos compact caption={t('Trusted by teams at companies and universities like')}/>

      {/* A curated, illustrated story says more than raw examples here. */}
      {isSocialStories ? (<>
          <SocialStoryExample />
          <SocialStoriesBotPromo />
        </>) : null}

      <TemplateHowItWorks preset={preset}/>

      {lang === 'en' ? <TemplateUseCases useCases={useCases}/> : null}

      {isSocialStories ? null : (<TemplateExamples preset={preset} examples={sectionExamples}/>)}

      <TemplateNextSteps recommendations={recommendations}/>

      {lang === 'en' ? (<TemplateFaq faqs={faqs} isClinical={isClinical}/>) : null}

      <TemplateRelated templates={otherPresets}/>

      <section className='pt-24 sm:pt-32'>
        <div className='mx-auto flex max-w-6xl flex-wrap items-end justify-between gap-6 px-4 sm:px-6 lg:px-8'>
          <SectionHeading size='md' title={t('Loved by people who create every day.')}/>
          <a href='https://www.trustpilot.com/review/easy-peasy.ai' target='_blank' rel='noopener nofollow noreferrer' className='flex-none'>
            <Image src='/images/stars-5.svg' alt={t('Our Trustpilot score')} width={160} height={32} loading='lazy'/>
          </a>
        </div>
        <div className='mx-auto mt-10 max-w-6xl px-0 sm:px-1 lg:px-3'>
          <Testimonials hideHeading/>
        </div>
      </section>

      <TemplateFinalCta preset={preset}/>
      <Footer />
    </main>);
};
export default Template;
export async function getStaticPaths() {
    // Presets flagged `isAbsoluteURL` live on their own landing page
    // (`landingPageSlug`) and only render a client-side redirect here, so there
    // is nothing worth prerendering for them. `fallback: true` still serves the
    // redirect on demand for any inbound link.
    const landingPresets = presets.filter((preset) => !preset.hideLandingPage && !preset.isAbsoluteURL);
    const locales = ['en', 'ja', 'es', 'fr', 'it', 'ko', 'zh', 'de'];
    return {
        paths: locales.flatMap((locale) => landingPresets.map((preset) => ({
            params: { slug: `${preset.slug}` },
            locale,
        }))),
        fallback: true,
    };
}
// The language name the example completions were saved under.
const COMPLETION_LANGUAGES: Record<string, string> = {
    es: 'Spanish',
    ja: 'Japanese',
    fr: 'French',
    it: 'Italian',
    de: 'German',
    ko: 'Korean',
    zh: 'Chinese',
    th: 'Thai',
    ar: 'Arabic',
    pt: 'Portuguese',
    ms: 'Malay',
};
const templateHref = (preset: Preset) => {
    if (!preset.isAbsoluteURL)
        return `/templates/${preset.slug.replace('/', '')}`;
    const path = preset.landingPageSlug || preset.toolHref || preset.slug;
    return path.startsWith('/') || path.startsWith('http') ? path : `/${path}`;
};
// Words too common to say two templates are related.
const COMMON_TITLE_WORDS = new Set([
    'ai',
    'and',
    'for',
    'the',
    'with',
    'your',
    'generator',
    'maker',
    'creator',
    'writer',
]);
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const locale = ctx.locale as string;
    const slug = ctx?.params?.slug as string;
    const lang = COMPLETION_LANGUAGES[locale] || '';
    const preset = presets.find((item) => item.slug === slug);
    if (!preset) {
        return { notFound: true };
    }
    let completions: TemplateExample[] = await getPublicCompletions(slug, lang);
    if (completions.length === 0) {
        completions = await getCompletionsFromPublicGenerations(slug, lang);
    }
    // The first example fills the output pane; make it the clearest one.
    completions = rankTemplateExamples(completions, locale);
    // Icons are components; they can't travel as props.
    const { icon: _icon, ...presetWithoutIcon } = preset;
    // Related templates: shared title words first ("Character", "Name"),
    // then the same category, then the rest of the catalogue.
    const titleWords = preset.title
        .split(/[^A-Za-z0-9]+/)
        .filter((word) => word.length > 2 && !COMMON_TITLE_WORDS.has(word.toLowerCase()));
    const categories = (cat: Preset['category']): PresetCategory[] => Array.isArray(cat) ? [...cat] : [cat as PresetCategory];
    const ownCategories = categories(preset.category);
    const sharesCategory = (p: Preset) => categories(p.category).some((cat) => ownCategories.includes(cat));
    const candidates = presets.filter((p) => p.slug !== preset.slug &&
        !p.hideLandingPage &&
        !p.isAbsoluteURL &&
        p.slug !== 'dynamic');
    const titleMatches = candidates
        .filter((p) => titleWords.some((word) => p.title.includes(word)))
        .slice(0, 12);
    const related = [
        ...titleMatches,
        ...candidates
            .filter((p) => !titleMatches.includes(p))
            .sort((a, b) => Number(sharesCategory(b)) - Number(sharesCategory(a)))
            .slice(0, 12 - titleMatches.length),
    ];
    // Only what the related cards show, not each preset's fields and flags.
    const otherPresets: RelatedTemplate[] = related.map((p) => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        href: templateHref(p),
        iconBackground: p.iconBackground,
        iconForeground: p.iconForeground,
    }));
    // Resolved here so the page doesn't ship the whole presets catalogue.
    const seen = new Set<string>();
    const recommendations: TemplateRecommendation[] = getRecommendationsForPreset(slug, [...presets], 3)
        // The curated lists repeat a few targets; one card per destination.
        .filter((rec) => {
        const key = rec.uri || rec.slug;
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    })
        .map((rec) => {
        const target = rec.preset;
        const recommendation: TemplateRecommendation = {
            slug: rec.slug,
            title: rec.title || target?.title || rec.slug,
            titleNs: rec.title ? 'recommendations' : 'tools',
            message: rec.message,
            href: rec.uri || (target ? templateHref(target) : `/presets/${rec.slug}`),
        };
        // Props can't hold `undefined`.
        if (rec.image)
            recommendation.image = rec.image;
        if (rec.video)
            recommendation.video = rec.video;
        if (target) {
            recommendation.iconBackground = target.iconBackground;
            recommendation.iconForeground = target.iconForeground;
        }
        return recommendation;
    });
    return {
        props: {
            completions,
            preset: presetWithoutIcon,
            locale,
            otherPresets,
            recommendations,
            samples: getTemplateSampleSet(slug),
            // Only the current template's content ships to the client (a few KB in
            // __NEXT_DATA__) instead of the full ~1MB corpora in the JS chunk.
            useCases: templateUseCases[slug] || [],
            faqs: templateFaqs[slug] || [],
            isClinical: CLINICAL_PRESET_SLUGS.includes(slug),
        },
    };
};

    async function __Next_Translate__getStaticProps__1a12e86ee97__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/templates/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ee97__ as getStaticProps }
  